
import React from 'react';
import { Product } from '../../../types';
import { Coffee, ChevronRight, Plus, Zap } from 'lucide-react';
import { StoreImage } from '../../ui/StoreImage';

interface CardProps {
  product: Product & { boostAmount?: number };
  onAddToCart: (product: Product) => void;
}

export const CardMinimal: React.FC<CardProps> = ({ product, onAddToCart }) => {
  const hasVariants = product.variants && product.variants.length > 0;
  
  let displayPrice = product.price;
  if (hasVariants) {
      const prices = product.variants!.map(v => v.price);
      displayPrice = Math.min(...prices);
  }

  const displayImage = product.images?.[0] || product.imageUrl;

  return (
    <div 
        onClick={() => onAddToCart(product)} 
        className="group bg-white rounded-2xl p-3 flex items-center gap-4 cursor-pointer border border-stone-100 hover:border-coffee-200 hover:shadow-md transition-all duration-200 h-full active:scale-[0.99] relative overflow-hidden"
    >
        {/* Hover Highlight Line */}
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-coffee-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>

        {/* Square Image */}
        <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden bg-stone-100 shrink-0 relative border border-stone-100 shadow-sm">
            {displayImage ? (
                <StoreImage
                    src={displayImage}
                    width={256}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
            ) : (
                <div className="w-full h-full flex items-center justify-center text-stone-300"><Coffee size={20}/></div>
            )}

            {/* Boost Badge */}
            {product.boostAmount && product.boostAmount > 0 && (
                <div className="absolute top-1 left-1 bg-rose-600/90 backdrop-blur-md text-white px-1.5 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest shadow-sm flex items-center gap-0.5 border border-white/20">
                    <Zap size={8} fill="currentColor" className="text-yellow-200" />
                    +{product.boostAmount}
                </div>
            )}
        </div>

        {/* Text Content */}
        <div className="flex-1 min-w-0 py-1 flex flex-col justify-center h-full">
            <h3 className="font-heading font-bold text-stone-900 text-sm md:text-base truncate group-hover:text-coffee-800 transition-colors mb-1">
                {product.name}
            </h3>
            
            <p className="text-stone-500 text-xs line-clamp-1 mb-2 font-medium leading-relaxed hidden md:block">
                {product.description}
            </p>
            
            <div className="flex items-center gap-2 mt-auto">
                <span className="font-mono font-bold text-stone-900 text-xs md:text-sm bg-stone-50 px-2 py-1 rounded-md group-hover:bg-coffee-50 group-hover:text-coffee-900 transition-colors border border-stone-100">
                    {displayPrice} kr
                </span>
                {hasVariants && <span className="text-[9px] text-stone-400 uppercase font-bold tracking-wider">Varianter</span>}
            </div>
        </div>

        {/* Action Icon */}
        <div className="w-10 h-10 rounded-full border border-stone-100 flex items-center justify-center text-stone-400 group-hover:border-coffee-500 group-hover:bg-coffee-500 group-hover:text-white transition-all shrink-0 mr-1 shadow-sm">
            {hasVariants ? <ChevronRight size={18} /> : <Plus size={20} />}
        </div>
    </div>
  );
};
